import "react-circular-progressbar/dist/styles.css";

import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { GetStaticPaths, GetStaticProps } from "next";
import React from "react";
import { withKeepAlive } from "react-next-keep-alive";

import { addApolloState, initializeApollo } from "@/apollo-client";
import Results, { FIRST } from "@/components/page/results/index";
import HOMEPAGE from "@/graphql/queries/homepage";
import SEARCH from "@/graphql/queries/search";
import { SearchQuery } from "@/graphql/types/graphql";
import { Search_Type } from "@/graphql/types/graphql";
import useTracker from "@/hooks/useTracker";
import { decodeURL, encodeURL } from "@/utils/index";
import { EDIT_LIST } from "@/utils/search";

interface EditsPageProps {
  preData: SearchQuery["search"];
  search: string;
  requestId: string;
}

const EditsPage = (props: EditsPageProps) => {
  const { editsPageViewEvent } = useTracker();
  return (
    <Results
      {...props}
      viewEvent={editsPageViewEvent}
      fetchPolicy="cache-only"
      isSSG
    />
  );
};

// @ts-expect-error router query is always string at here
export default withKeepAlive(EditsPage, ({ router }) => router.query.search, {
  // Disabled this to avoid conflict with the nextjs scroll restoration
  keepScrollEnabled: false,
});

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.NEXT_PUBLIC_SSG === "false") {
    return {
      paths: [],
      fallback: "blocking",
    };
  }

  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query({
    query: HOMEPAGE,
  });

  let homeCarousel = [];

  if (data && data.homepage) {
    homeCarousel = data.homepage.reduce((acc: any, cur: any) => {
      return acc.concat(cur.items.map((item: any) => item.searchQuery));
    }, []);
  }

  const generalPaths = EDIT_LIST.general
    .filter((search) => !!search)
    .map((search) => {
      return {
        params: {
          search: encodeURL(search),
        },
      };
    });

  const itemPaths = EDIT_LIST.item
    .concat(homeCarousel)
    .filter((search) => !!search)
    .map((search) => {
      return {
        params: {
          search: encodeURL(search),
          type: Search_Type.Item,
        },
      };
    });

  const paths = generalPaths.concat(itemPaths);

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps<any> = async ({ params }) => {
  if (!params) return;

  const apolloClient: ApolloClient<NormalizedCacheObject> = initializeApollo();
  const search = decodeURL(params.search as string);
  const type = decodeURL(params.type as string);

  const { data } = await apolloClient.query<SearchQuery>({
    query: SEARCH,
    variables: {
      query: search,
      first: FIRST,
      skipPersonalization: true,
      useOwnSize: false,
      type: type || undefined,
    },
  });

  return addApolloState(apolloClient, {
    props: {
      search,
      preData: data?.search,
      requestId: data?.search?.items?.searchID || "",
    },
    revalidate: 60 * 60 * 24,
  });
};
